import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Bell } from "lucide-react";
import { api } from "../lib/api";
import { MaskedLines, FadeUp } from "../components/Reveal";
import { DealCard } from "../components/DealCard";
import { GatedPrice } from "../components/GatedPrice";
import { Countdown } from "../components/Countdown";
import { TickerMarquee, TrustStrip } from "../components/Strips";
import { SoldTicker } from "../components/SoldTicker";
import { NotifyButton } from "../components/NotifyButton";
import { useAuth } from "../context/AuthContext";

const MANIFESTO = [
  ["01", "WE BUY DIRECT.", "Retail returns, end-of-line and overstock straight from UK retailers and distributors. No middlemen taking a cut."],
  ["02", "YOU BUY CHEAP.", "Member prices sit 60–85% under RRP. Verified manifests so you know exactly what's on the pallet before you commit."],
  ["03", "GONE WHEN IT'S GONE.", "One hero deal a day. Limited lots. When the counter hits zero or stock runs out, it's finished. No re-runs."],
];

export default function Home() {
  const { isMember } = useAuth();
  const [hero, setHero] = useState(null);
  const [deals, setDeals] = useState([]);
  const [coming, setComing] = useState([]);
  const [cats, setCats] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  useEffect(() => {
    api.get("/deals/hero").then(({ data }) => setHero(data)).catch(() => {});
    api.get("/deals").then(({ data }) => setDeals(data.filter((d) => !d.is_hero).slice(0, 4)));
    api.get("/deals", { params: { status: "coming_soon" } }).then(({ data }) => setComing(data));
    api.get("/categories").then(({ data }) => setCats(data));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative border-b border-[#27272A] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[#FF3B30] text-xs uppercase tracking-[0.3em] mb-6">UK Job Lots · Clearance · Pallets</div>
            <MaskedLines
              lines={["ONE DEAL", "A DAY.", "GONE WHEN", "IT'S GONE."]}
              className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.9]"
            />
            <motion.p
              className="text-white/50 text-sm sm:text-base mt-8 max-w-md leading-relaxed"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.8 }}
            >
              Trade-priced job lots and pallets, direct from UK retailers. Free membership unlocks every price on the site.
            </motion.p>
            <motion.div className="flex flex-col sm:flex-row gap-4 mt-10" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.6 }}>
              {!isMember && (
                <Link to="/join" data-testid="hero-join-button" className="btn-industrial px-8 py-4 text-xl text-center">
                  UNLOCK MEMBER PRICES
                </Link>
              )}
              <Link to="/deals" data-testid="hero-browse-button" className="btn-ghost-industrial px-8 py-4 text-xl text-center">
                BROWSE TODAY'S DEALS
              </Link>
            </motion.div>
          </div>

          {hero && (
            <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
              <Link to={`/deal/${hero.slug}`} data-testid="hero-deal-card" className="block border-2 border-[#FFCC00] bg-[#111111] relative group">
                <div className="absolute -top-3 left-6 bg-[#FF3B30] text-white text-[11px] uppercase tracking-widest px-3 py-1 z-10 font-bold">
                  Today's Hero Deal
                </div>
                <div className="overflow-hidden aspect-[16/10]">
                  <motion.img style={{ y: imgY }} src={hero.images[0]} alt={hero.title} className="w-full h-[115%] object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 sm:p-8">
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">{hero.sku} · {hero.condition} · {hero.units} units</div>
                  <h2 className="font-display text-2xl sm:text-3xl leading-tight mb-4">{hero.title}</h2>
                  <div className="flex items-end justify-between gap-4 flex-wrap">
                    <GatedPrice deal={hero} size="lg" />
                    <div>
                      <div className="text-[10px] uppercase text-white/40 mb-1">Deal ends in</div>
                      <Countdown endsAt={hero.ends_at} size="lg" />
                    </div>
                  </div>
                  <div className="mt-4 h-1.5 bg-[#27272A]">
                    <div className="h-full bg-[#FF3B30]" style={{ width: `${(hero.stock_remaining / hero.stock_total) * 100}%` }} />
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-[#FF3B30] mt-2">{hero.stock_remaining} of {hero.stock_total} lots remaining</div>
                </div>
              </Link>
            </motion.div>
          )}
        </div>
      </section>

      <SoldTicker />
      <TickerMarquee />

      {/* MANIFESTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20 sm:py-28">
        <div className="grid md:grid-cols-3 gap-px bg-[#27272A] border border-[#27272A]">
          {MANIFESTO.map(([n, title, body], i) => (
            <FadeUp key={n} delay={i * 0.12} className="bg-[#0A0A0A] p-8 sm:p-12">
              <div className="font-display text-6xl text-[#27272A] mb-6">{n}</div>
              <h3 className="font-display text-2xl sm:text-3xl text-[#FFCC00] mb-4">{title}</h3>
              <p className="text-white/50 text-sm leading-relaxed">{body}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* SUPPORTING DEALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-20">
        <FadeUp className="flex items-end justify-between mb-10">
          <h2 className="font-display text-3xl sm:text-4xl">LIVE <span className="text-[#FFCC00]">SUPPORTING</span> DEALS</h2>
          <Link to="/deals" data-testid="view-all-deals-link" className="text-xs uppercase tracking-widest text-white/50 hover:text-[#FFCC00] flex items-center gap-2">
            View all <ArrowRight size={14} />
          </Link>
        </FadeUp>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {deals.map((d, i) => <DealCard key={d.id} deal={d} index={i} />)}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-20">
        <FadeUp><h2 className="font-display text-3xl sm:text-4xl mb-10">SHOP BY <span className="text-[#FFCC00]">CATEGORY</span></h2></FadeUp>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#27272A] border border-[#27272A]">
          {cats.map((c) => (
            <Link key={c.slug} to={`/deals?category=${c.slug}`} data-testid={`category-tile-${c.slug}`} className="bg-[#0A0A0A] p-6 sm:p-8 hover:bg-[#FFCC00] hover:text-black group transition-colors duration-150">
              <div className="font-display text-xl leading-tight">{c.name}</div>
              <div className="text-xs mt-2 text-white/40 group-hover:text-black/60">{c.deal_count} live</div>
            </Link>
          ))}
        </div>
      </section>

      {/* COMING SOON */}
      {coming.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-20">
          <FadeUp className="flex items-center gap-3 mb-10">
            <Bell size={18} className="text-[#FF3B30]" />
            <h2 className="font-display text-3xl sm:text-4xl">NEXT WEEK'S <span className="text-[#FF3B30]">DROPS</span></h2>
          </FadeUp>
          <div className="grid sm:grid-cols-2 gap-6">
            {coming.map((d, i) => (
              <FadeUp key={d.id} delay={i * 0.1}>
                <div className="border border-[#27272A] bg-[#111111] flex flex-wrap gap-5 p-5 items-center">
                  <img src={d.images[0]} alt={d.title} className="w-24 h-24 object-cover grayscale" />
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-[10px] uppercase tracking-widest text-[#FF3B30] mb-1">Coming Soon</div>
                    <div className="font-display text-xl leading-tight">{d.title}</div>
                    <div className="text-white/40 text-xs mt-1">{d.condition} · {d.units} units</div>
                  </div>
                  <NotifyButton deal={d} />
                </div>
              </FadeUp>
            ))}
          </div>
        </section>
      )}

      {/* CLUB BLOCK */}
      {!isMember && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-20">
          <FadeUp>
            <div className="border-2 border-[#FFCC00] p-10 sm:p-16 relative overflow-hidden">
              <div className="hazard-stripes absolute top-0 left-0 right-0 h-2" />
              <h2 className="font-display text-4xl sm:text-6xl leading-none">JOIN THE <span className="text-[#FFCC00]">UK JOBLOT CLUB.</span></h2>
              <p className="text-white/50 text-sm sm:text-base mt-4 max-w-lg">Free membership. Every price unlocked. First dibs on hero deals before the public preview.</p>
              <Link to="/join" data-testid="club-join-button" className="btn-industrial inline-block px-10 py-4 text-xl mt-8">JOIN FREE — 30 SECONDS</Link>
            </div>
          </FadeUp>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-8">
        <TrustStrip />
      </section>
    </div>
  );
}
